import { DataMatrixSnapshot } from '@6grain/masofa-monolith-api-client/common/model/dataMatrixSnapshot';
import { DataMatrixLayerDates } from '@6grain/masofa-monolith-api-client/common/model/dataMatrixLayerDates';
import { DataMatrixSceneCount } from '@6grain/masofa-monolith-api-client/common/model/dataMatrixSceneCount';
import { yearlyAtdDropdownTitleKey } from '@app/pages/weather/pages/gis-weather/components/weather-legend/legend-match.util';

/**
 * Data monitoring matrix (docs/ORCHESTRATION.md §8 "Data monitoring matrix"), ported from the AgroHub Admin
 * `coverage-matrix.ts`. The backend returns which days have data; every cell rule lives here.
 *  - `ok`      the data is there;
 *  - `warn`    something is pending, and that is not (yet) a fault;
 *  - `problem` the data should be there and is not, or a scene failed / got stuck;
 *  - `none`    nothing is expected that day, and nothing is there.
 */
export type CellState = 'ok' | 'warn' | 'problem' | 'none';

export type CellNote = 'missing' | 'failed' | 'stuck' | 'inProgress' | 'notDownloaded' | 'noIndexLayer' | 'noForecast' | 'forecast';

export type MatrixCell = {
  state: CellState;
  note: CellNote | null;
  /** Scenes of the day, for the satellite rows. */
  count?: number;
};

export type MatrixGroupKey =
  'reports' | 'weather' | 'deviation' | 'normalized' | 'yearly' | 'satelliteDownloaded' | 'satellite' | 'indices' | 'anomalies';

export type MatrixRow = {
  /** Stable across reloads, so the page can keep a row highlighted. */
  key: string;
  /** Translation key, or the text itself when `translateLabel` is false. */
  label: string;
  translateLabel: boolean;
  /** yyyy-MM-dd -> cell, for every day of the year. */
  cells: Record<string, MatrixCell>;
};

export type MatrixGroup = { key: MatrixGroupKey; rows: MatrixRow[] };
export type Matrix = { year: number; groups: MatrixGroup[] };

export type MonthSummary = {
  /** 0-11. */
  month: number;
  problems: number;
  /** Rows with problems in the month, the worst first. */
  rows: { row: MatrixRow; days: number }[];
};

export type DataMatrixSource = 'era' | 'ibm' | 'tiles' | 'indices' | 'satellite' | 'anomaly';

export type SourceFailure = { source: DataMatrixSource; labelKey: string; error: string };

/** A daily report or deviation layer is due once the day is over. */
export const REPORT_LAG_DAYS = 1;

/** Weather tiles are drawn this many days ahead from the forecast; today .. today + 6 are due. */
export const FORECAST_DAYS_AHEAD = 7;

/** A scene still short of Processed after this many days is stuck rather than in progress. */
export const STUCK_AFTER_DAYS = 2;

/** How often the page reloads on its own. */
export const DATA_MONITORING_REFRESH_MS = 30 * 60 * 1000;

export const INDEX_NAMES = ['ARVI', 'EVI', 'GNDVI', 'MNDWI', 'NDWI', 'NDMI', 'NDVI', 'ORVI', 'OSAVI'] as const;

const SUM_OF_ACTIVE_TEMPERATURES = [5, 7, 10, 12, 15].map(base => `SumOfActiveTemperaturesBase${base}`);

/**
 * The tile rows are the ERA5 "Weather layer" values of each GIS "Data type" (`/weather/gis`), as rendered on the
 * instance. Masofa draws no daily `TemperatureMin/Max/MinTotal/MaxTotal` weather tiles, so they are not rows here.
 */
export const WEATHER_LAYERS: readonly string[] = [
  'TemperatureAverage',
  'FrostDanger',
  'Fallout',
  'Humidity',
  'SolarRadiationInfluence',
  ...SUM_OF_ACTIVE_TEMPERATURES,
  'SumOfSolarRadiation',
  'SumOfFallout',
];

export const DEVIATION_LAYERS: readonly string[] = [
  'TemperatureAverage',
  'TemperatureMin',
  'TemperatureMinTotal',
  'TemperatureMax',
  'TemperatureMaxTotal',
  ...SUM_OF_ACTIVE_TEMPERATURES,
];

export const NORMALIZED_LAYERS: readonly string[] = [
  'TemperatureAverage',
  'SolarRadiationInfluence',
  'Fallout',
  'Humidity',
  ...SUM_OF_ACTIVE_TEMPERATURES,
  'SumOfSolarRadiation',
  'SumOfFallout',
];

/** Yearly ERA layers (`Era{type}{layer}{yyyy}`) the instance renders, in display order. */
export const YEARLY_LAYERS: readonly { type: 'Weather' | 'Deviation'; layer: string }[] = [
  { type: 'Weather', layer: 'SumOfFallout' },
  { type: 'Weather', layer: 'TemperatureAverageTotal' },
  { type: 'Deviation', layer: 'SumOfFallout' },
  { type: 'Deviation', layer: 'TemperatureAverageTotal' },
];

/** Translation key of each group header. */
export const GROUP_LABEL_KEYS: Readonly<Record<MatrixGroupKey, string>> = {
  reports: 'DATA_MONITORING_GROUP_REPORTS',
  weather: 'DATA_MONITORING_GROUP_WEATHER',
  deviation: 'DATA_MONITORING_GROUP_DEVIATION',
  normalized: 'DATA_MONITORING_GROUP_NORMALIZED',
  yearly: 'DATA_MONITORING_GROUP_YEARLY',
  satelliteDownloaded: 'DATA_MONITORING_GROUP_SATELLITE_DOWNLOADED',
  satellite: 'DATA_MONITORING_GROUP_SATELLITE',
  indices: 'DATA_MONITORING_GROUP_INDICES',
  anomalies: 'DATA_MONITORING_GROUP_ANOMALIES',
};

const SOURCE_LABEL_KEYS: Record<DataMatrixSource, string> = {
  era: 'DATA_MONITORING_SOURCE_ERA',
  ibm: 'DATA_MONITORING_SOURCE_IBM',
  tiles: 'DATA_MONITORING_SOURCE_TILES',
  indices: 'DATA_MONITORING_SOURCE_INDICES',
  satellite: 'DATA_MONITORING_SOURCE_SATELLITE',
  anomaly: 'DATA_MONITORING_SOURCE_ANOMALY',
};

const OK: MatrixCell = { state: 'ok', note: null };
const NONE: MatrixCell = { state: 'none', note: null };
const MISSING: MatrixCell = { state: 'problem', note: 'missing' };
const FORECAST: MatrixCell = { state: 'ok', note: 'forecast' };
const NO_FORECAST: MatrixCell = { state: 'problem', note: 'noForecast' };

const MS_PER_DAY = 86_400_000;
const SEVERITY: Record<CellState, number> = { none: 0, ok: 1, warn: 2, problem: 3 };

const pad = (n: number): string => String(n).padStart(2, '0');
const toMs = (iso: string): number => Date.parse(`${iso}T00:00:00Z`);

/** Today in UTC, yyyy-MM-dd: the day the backend jobs and the matrix rules count from. */
export function currentUtcDay(): string {
  return new Date().toISOString().slice(0, 10);
}

export function addDays(iso: string, days: number): string {
  return new Date(toMs(iso) + days * MS_PER_DAY).toISOString().slice(0, 10);
}

function daysBetween(from: string, to: string): number {
  return Math.round((toMs(to) - toMs(from)) / MS_PER_DAY);
}

/** Every date (yyyy-MM-dd) of `monthIndex` (0-11) in `year`. */
export function monthDays(year: number, monthIndex: number): string[] {
  const count = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  return Array.from({ length: count }, (_, i) => `${year}-${pad(monthIndex + 1)}-${pad(i + 1)}`);
}

/** 0 (Sunday) .. 6 (Saturday) of a yyyy-MM-dd date. */
export function weekdayOf(iso: string): number {
  return new Date(toMs(iso)).getUTCDay();
}

function yearDays(year: number): string[] {
  return Array.from({ length: 12 }, (_, month) => monthDays(year, month)).flat();
}

function cellsFor(days: string[], cellOf: (iso: string) => MatrixCell): Record<string, MatrixCell> {
  const cells: Record<string, MatrixCell> = {};
  for (const iso of days) {
    cells[iso] = cellOf(iso);
  }
  return cells;
}

const dayOnly = (value: string | null | undefined): string | null => value?.slice(0, 10) ?? null;

function layerDays(layers: DataMatrixLayerDates[] | null | undefined): Map<string, Set<string>> {
  const byLayer = new Map<string, Set<string>>();
  for (const item of layers ?? []) {
    if (item.layer) {
      byLayer.set(item.layer, new Set((item.dates ?? []).map(date => date.slice(0, 10))));
    }
  }
  return byLayer;
}

/** Sources whose block came back with an error: their rows are left out of the matrix. */
export function sourceFailures(snapshot: DataMatrixSnapshot): SourceFailure[] {
  const blocks: [DataMatrixSource, string | null | undefined][] = [
    ['era', snapshot.era?.error],
    ['ibm', snapshot.ibm?.error],
    ['tiles', snapshot.tiles?.error],
    ['indices', snapshot.indices?.error],
    ['satellite', snapshot.satellite?.error],
    ['anomaly', snapshot.anomaly?.error],
  ];
  return blocks
    .filter((entry): entry is [DataMatrixSource, string] => !!entry[1])
    .map(([source, error]) => ({ source, labelKey: SOURCE_LABEL_KEYS[source], error }));
}

/** Data due every day from `from` to `lastDue`; absent inside that window is a problem, outside it is nothing. */
function dailyRow(
  key: string,
  label: string,
  days: string[],
  from: string | null,
  lastDue: string,
  has: (iso: string) => boolean,
): MatrixRow {
  return {
    key,
    label,
    translateLabel: true,
    cells: cellsFor(days, iso => (has(iso) ? OK : from !== null && iso >= from && iso <= lastDue ? MISSING : NONE)),
  };
}

/** Days before today are facts; today up to the forecast horizon is due as a forecast; beyond it a tile is only accepted. */
function weatherRow(
  layer: string,
  days: string[],
  from: string | null,
  lastDue: string,
  today: string,
  has: (iso: string) => boolean,
): MatrixRow {
  const forecastDue = addDays(today, FORECAST_DAYS_AHEAD - 1);
  return {
    key: `weather:${layer}`,
    label: layer,
    translateLabel: true,
    cells: cellsFor(days, iso => {
      const inWindow = from !== null && iso >= from;
      if (iso >= today) {
        // Today's tile may be built from the day's own reports, so only the days after it are a forecast.
        return has(iso) ? (iso === today ? OK : FORECAST) : inWindow && iso <= forecastDue ? NO_FORECAST : NONE;
      }
      return has(iso) ? OK : inWindow && iso <= lastDue ? MISSING : NONE;
    }),
  };
}

/** The "processed" rows: a day has data once a scene reaches Processed (the AgroHub rule). */
function sceneCell(scenes: DataMatrixSceneCount[], day: string, today: string): MatrixCell {
  const count = scenes.reduce((sum, scene) => sum + (scene.count ?? 0), 0);
  const stuck = daysBetween(day, today) > STUCK_AFTER_DAYS;
  let processed = 0;
  let inProgress = false;
  let stalled = false;
  let failed = false;

  for (const scene of scenes) {
    switch (scene.stage) {
      case 'Processed':
        processed += scene.count ?? 0;
        break;
      case 'ProcessingFailed':
      case 'DownloadFailed':
        failed = true;
        break;
      case 'Downloading':
      case 'Downloaded':
      case 'Processing':
        if (stuck) {
          stalled = true;
        } else {
          inProgress = true;
        }
        break;
      // Discovered (e.g. above the cloud-cover limit) is never a fault; it only decides a day with nothing else.
    }
  }

  if (failed) {
    return { state: 'problem', note: 'failed', count };
  }
  if (stalled) {
    return { state: 'problem', note: 'stuck', count };
  }
  if (inProgress) {
    return { state: 'warn', note: 'inProgress', count };
  }
  return processed > 0 ? { state: 'ok', note: null, count } : { state: 'warn', note: 'notDownloaded', count };
}

/** Stages a scene reaches only after its archive is downloaded. */
const DOWNLOADED_STAGES: ReadonlySet<string> = new Set(['Downloaded', 'Processing', 'Processed', 'ProcessingFailed']);

/** The "downloaded" rows: a day has data once any of its scenes is downloaded, whatever happens to processing. */
function downloadedSceneCell(scenes: DataMatrixSceneCount[], day: string, today: string): MatrixCell {
  const count = scenes.reduce((sum, scene) => sum + (scene.count ?? 0), 0);
  const stages = new Set(scenes.filter(scene => (scene.count ?? 0) > 0).map(scene => scene.stage ?? ''));

  if ([...stages].some(stage => DOWNLOADED_STAGES.has(stage))) {
    return { state: 'ok', note: null, count };
  }
  if (stages.has('DownloadFailed')) {
    return { state: 'problem', note: 'failed', count };
  }
  if (stages.has('Downloading')) {
    return daysBetween(day, today) > STUCK_AFTER_DAYS
      ? { state: 'problem', note: 'stuck', count }
      : { state: 'warn', note: 'inProgress', count };
  }
  return { state: 'warn', note: 'notDownloaded', count };
}

type SceneCellRule = (scenes: DataMatrixSceneCount[], day: string, today: string) => MatrixCell;

function sceneRow(
  key: string,
  label: string,
  scenes: DataMatrixSceneCount[],
  days: string[],
  today: string,
  cellOf: SceneCellRule,
): MatrixRow {
  const byDay = new Map<string, DataMatrixSceneCount[]>();
  for (const scene of scenes) {
    const day = dayOnly(scene.date) ?? '';
    byDay.set(day, [...(byDay.get(day) ?? []), scene]);
  }
  return {
    key,
    label,
    translateLabel: true,
    cells: cellsFor(days, iso => {
      const dayScenes = byDay.get(iso);
      return dayScenes ? cellOf(dayScenes, iso, today) : NONE;
    }),
  };
}

/**
 * A yearly layer has one cell, on the day the year's layer is due: 31 December of a completed year, or yesterday
 * for the current year's weather layer. Yearly deviation is drawn only for completed years.
 */
function yearlyRow(
  type: 'Weather' | 'Deviation',
  layer: string,
  year: number,
  days: string[],
  from: string | null,
  lastDue: string,
  present: boolean,
): MatrixRow {
  const yearEnd = `${year}-12-31`;
  const completed = yearEnd <= lastDue;
  const dueDay = completed ? yearEnd : type === 'Weather' && lastDue.startsWith(`${year}-`) ? lastDue : null;
  return {
    key: `yearly:${type}:${layer}`,
    label: yearlyAtdDropdownTitleKey(`Era${type}${layer}${year}`) ?? `${type}${layer}`,
    translateLabel: true,
    cells: cellsFor(days, iso => {
      if (iso !== dueDay) {
        return NONE;
      }
      return present ? OK : from !== null && iso >= from ? MISSING : NONE;
    }),
  };
}

function anomalyRunCell(state: string | null | undefined): MatrixCell {
  switch (state) {
    case 'Completed':
      return OK;
    case 'Pending':
    case 'Running':
      return { state: 'warn', note: 'inProgress' };
    case 'Failed':
    case 'Cancelled':
      return { state: 'problem', note: 'failed' };
    default:
      return MISSING;
  }
}

/**
 * The matrix of the page: one row per monitored parameter, one cell per day of `year`.
 * `today` (yyyy-MM-dd, UTC) is a parameter so the rules stay testable.
 */
export function buildMatrix(snapshot: DataMatrixSnapshot, year: number, today: string): Matrix {
  const days = yearDays(year);
  const lastDue = addDays(today, -REPORT_LAG_DAYS);
  const failed = new Set(sourceFailures(snapshot).map(failure => failure.source));
  const eraFrom = dayOnly(snapshot.era?.dataFrom);
  const groups: MatrixGroup[] = [];

  const reports: MatrixRow[] = [];
  if (!failed.has('era')) {
    const eraDates = new Set((snapshot.era?.dates ?? []).map(date => date.slice(0, 10)));
    reports.push(dailyRow('era', 'DATA_MONITORING_ROW_ERA', days, eraFrom, lastDue, iso => eraDates.has(iso)));
  }
  if (!failed.has('ibm')) {
    const ibmDates = new Set((snapshot.ibm?.dates ?? []).map(date => date.slice(0, 10)));
    const ibmFrom = dayOnly(snapshot.ibm?.dataFrom);
    reports.push(dailyRow('ibm', 'DATA_MONITORING_ROW_IBM', days, ibmFrom, lastDue, iso => ibmDates.has(iso)));
  }
  groups.push({ key: 'reports', rows: reports });

  if (!failed.has('tiles')) {
    const weather = layerDays(snapshot.tiles?.weather);
    const deviation = layerDays(snapshot.tiles?.deviation);
    const normalized = layerDays(snapshot.tiles?.normalized);
    groups.push({
      key: 'weather',
      rows: WEATHER_LAYERS.map(layer =>
        weatherRow(layer, days, eraFrom, lastDue, today, iso => weather.get(layer)?.has(iso) ?? false)),
    });
    groups.push({
      key: 'deviation',
      // Deviation is a fact minus the norm, so it follows the facts and stops at yesterday.
      rows: DEVIATION_LAYERS.map(layer => ({
        ...dailyRow(`deviation:${layer}`, layer, days, eraFrom, lastDue, iso => deviation.get(layer)?.has(iso) ?? false),
      })),
    });
    groups.push({
      key: 'normalized',
      // A norm belongs to a calendar day of no particular year, so every day of the year is due.
      rows: NORMALIZED_LAYERS.map(layer => ({
        key: `normalized:${layer}`,
        label: layer,
        translateLabel: true,
        cells: cellsFor(days, iso => (normalized.get(layer)?.has(iso) ? OK : MISSING)),
      })),
    });
    const yearly = new Set((snapshot.tiles?.yearly ?? []).map(item => `${item.type}:${item.layer}`));
    groups.push({
      key: 'yearly',
      rows: YEARLY_LAYERS.map(({ type, layer }) =>
        yearlyRow(type, layer, year, days, eraFrom, lastDue, yearly.has(`${type}:${layer}`))),
    });
  }

  const scenes = failed.has('satellite') ? [] : snapshot.satellite?.scenes ?? [];
  const scenesOf = (source: string): DataMatrixSceneCount[] => scenes.filter(scene => scene.source === source);
  if (!failed.has('satellite')) {
    groups.push({
      key: 'satelliteDownloaded',
      rows: [
        sceneRow('downloaded:sentinel', 'DATA_MONITORING_ROW_SENTINEL', scenesOf('Sentinel2'), days, today, downloadedSceneCell),
        sceneRow('downloaded:landsat', 'DATA_MONITORING_ROW_LANDSAT', scenesOf('Landsat'), days, today, downloadedSceneCell),
      ],
    });
    groups.push({
      key: 'satellite',
      rows: [
        sceneRow('sentinel', 'DATA_MONITORING_ROW_SENTINEL', scenesOf('Sentinel2'), days, today, sceneCell),
        sceneRow('landsat', 'DATA_MONITORING_ROW_LANDSAT', scenesOf('Landsat'), days, today, sceneCell),
      ],
    });
  }

  // Indices are computed from Sentinel-2 only: a day is due when it has a processed Sentinel-2 scene.
  const processedDays = new Set(
    scenesOf('Sentinel2')
      .filter(scene => scene.stage === 'Processed' && (scene.count ?? 0) > 0)
      .map(scene => dayOnly(scene.date) ?? ''),
  );

  if (!failed.has('indices')) {
    const indexLayers = layerDays(snapshot.indices?.layers);
    groups.push({
      key: 'indices',
      rows: INDEX_NAMES.map(name => {
        const layerDaysOfIndex = indexLayers.get(name) ?? new Set<string>();
        // An index with no layer in the year is one this instance does not produce: not 365 red days.
        const produced = layerDaysOfIndex.size > 0;
        return {
          key: `index:${name}`,
          label: name,
          translateLabel: false,
          cells: cellsFor(days, (iso): MatrixCell => {
            if (!processedDays.has(iso)) {
              return NONE;
            }
            if (layerDaysOfIndex.has(iso)) {
              return OK;
            }
            return produced ? { state: 'problem', note: 'noIndexLayer' } : NONE;
          }),
        };
      }),
    });
  }

  if (!failed.has('anomaly')) {
    const runsByDate = new Map((snapshot.anomaly?.runs ?? []).map(run => [dayOnly(run.date) ?? '', run.state]));
    groups.push({
      key: 'anomalies',
      rows: [{
        key: 'vegetation-anomaly',
        label: 'DATA_MONITORING_ROW_VEGETATION_ANOMALY',
        translateLabel: true,
        cells: cellsFor(days, iso =>
          !processedDays.has(iso) || iso > lastDue ? NONE : anomalyRunCell(runsByDate.get(iso))),
      }],
    });
  }

  return { year, groups: groups.filter(group => group.rows.length > 0) };
}

/** The worst state among a group's rows on one day - what the group header shows. */
export function groupDayState(group: MatrixGroup, iso: string): CellState {
  let worst: CellState = 'none';
  for (const row of group.rows) {
    const state = row.cells[iso]?.state ?? 'none';
    if (SEVERITY[state] > SEVERITY[worst]) {
      worst = state;
    }
  }
  return worst;
}

export function problemDays(row: MatrixRow, days: string[]): number {
  return days.filter(iso => row.cells[iso]?.state === 'problem').length;
}

/** Per month of the year: how many problem cells there are and which rows hold them. */
export function monthSummaries(matrix: Matrix): MonthSummary[] {
  const rows = matrix.groups.flatMap(group => group.rows);
  return Array.from({ length: 12 }, (_, month) => {
    const days = monthDays(matrix.year, month);
    const withProblems = rows
      .map(row => ({ row, days: problemDays(row, days) }))
      .filter(entry => entry.days > 0)
      // Array.prototype.sort is stable, so equal counts keep the matrix order.
      .sort((a, b) => b.days - a.days);
    return { month, problems: withProblems.reduce((sum, entry) => sum + entry.days, 0), rows: withProblems };
  });
}

/** The month the board opens on: this month in the current year, otherwise the first month with a problem. */
export function defaultMonth(summaries: MonthSummary[], year: number, today: string): number {
  if (year === Number(today.slice(0, 4))) {
    return Number(today.slice(5, 7)) - 1;
  }
  const firstProblem = summaries.find(summary => summary.problems > 0);
  return firstProblem?.month ?? 0;
}
